import { DotsThree, Plus, Scales } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AUTH_ROLE_PERMISSIONS, type AuthRole } from "~/api/governanceTypes";
import {
  useAuthRoles,
  useCreateAuthRole,
  useDeleteAuthRole,
  useUpdateAuthRole,
} from "~/api/queries";
import { Row, Rows } from "~/features/automation/components";
import { TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface RoleForm {
  name: string;
  slug: string;
  description: string;
  permissions: string[];
}

function RoleDialog({ role, onClose }: { role?: AuthRole; onClose: () => void }) {
  const { t } = useTranslation();
  const createRole = useCreateAuthRole();
  const updateRole = useUpdateAuthRole();
  const [form, setForm] = useState<RoleForm>({
    name: role?.name ?? "",
    slug: role?.slug ?? "",
    description: role?.description ?? "",
    permissions: role?.permissions ?? [],
  });
  const [slugTouched, setSlugTouched] = useState(Boolean(role));
  const [extra, setExtra] = useState("");

  const isEdit = Boolean(role);
  const busy = createRole.isPending || updateRole.isPending;

  const setName = (name: string) => {
    setForm((prev) => ({ ...prev, name, slug: slugTouched ? prev.slug : slugify(name) }));
  };

  const togglePermission = (permission: string) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const addExtra = () => {
    const value = extra.trim();
    if (value && !form.permissions.includes(value)) {
      setForm((prev) => ({ ...prev, permissions: [...prev.permissions, value] }));
    }
    setExtra("");
  };

  const submit = () => {
    if (!form.name.trim() || !form.slug.trim()) {
      return;
    }
    const shared = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim() || undefined,
      permissions: form.permissions,
    };
    if (isEdit && role) {
      updateRole.mutate(
        { roleId: role.id, ...shared },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Role saved"));
          },
        },
      );
    } else {
      createRole.mutate(shared, {
        onSuccess: () => {
          onClose();
          toast.success(t("Role created"));
        },
      });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? t("Edit role") : t("New role")}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!form.name.trim() || busy} onClick={submit}>
            {isEdit ? t("Save") : t("Create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t("Name")}
          fullWidth
          value={form.name}
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label={t("Slug")}
          fullWidth
          value={form.slug}
          onChange={(event) => {
            setSlugTouched(true);
            setForm((prev) => ({ ...prev, slug: event.target.value }));
          }}
        />
        <TextArea
          label={t("Description")}
          rows={2}
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        />
        <div className="flex flex-col gap-1.5">
          <Text variant="small" className="font-medium text-text-secondary">
            {t("Permissions")}
          </Text>
          <p className="text-small text-text-tertiary">
            {t("These are recorded as written. Enforcement uses Docit.Auth's own scopes.")}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {AUTH_ROLE_PERMISSIONS.map((permission) => (
              <label
                key={permission}
                className="flex cursor-pointer items-center gap-1.5 text-small text-text"
              >
                <input
                  type="checkbox"
                  checked={form.permissions.includes(permission)}
                  onChange={() => togglePermission(permission)}
                />
                <span className="font-mono">{permission}</span>
              </label>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <Input
              label={t("Add a permission")}
              fullWidth
              value={extra}
              onChange={(event) => setExtra(event.target.value)}
            />
            <Button variant="neutral" onClick={addExtra}>
              {t("Add")}
            </Button>
          </div>
          {form.permissions.filter((p) => !(AUTH_ROLE_PERMISSIONS as readonly string[]).includes(p))
            .length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {form.permissions
                .filter((p) => !(AUTH_ROLE_PERMISSIONS as readonly string[]).includes(p))
                .map((p) => (
                  <span
                    key={p}
                    className="rounded-sm bg-bg-secondary px-1.5 py-0.5 font-mono text-small"
                  >
                    {p}
                  </span>
                ))}
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

export function RolesSection() {
  const { t } = useTranslation();
  const { data: roles, isLoading } = useAuthRoles();
  const deleteRole = useDeleteAuthRole();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AuthRole | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AuthRole | null>(null);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
          {t("New role")}
        </Button>
      </div>

      {!roles?.length ? (
        <Empty icon={Scales} message={t("No roles yet")} />
      ) : (
        <Rows>
          {roles.map((role) => {
            const items: MenuItem[] = role.isSystem
              ? []
              : [
                  { type: "button", title: t("Edit…"), onClick: () => setEditing(role) },
                  {
                    type: "button",
                    title: t("Delete…"),
                    dangerous: true,
                    onClick: () => setConfirmDelete(role),
                  },
                ];
            return (
              <Row key={role.id}>
                <Scales size={18} aria-hidden className="shrink-0 text-text-tertiary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">
                    {role.name}
                    {role.isSystem ? (
                      <span className="ms-2 rounded-sm bg-bg-secondary px-1.5 py-0.5 text-small text-text-tertiary">
                        {t("System")}
                      </span>
                    ) : null}
                  </p>
                  <p className="truncate text-small text-text-tertiary">
                    {role.permissions.join(", ")}
                  </p>
                </div>
                {items.length > 0 ? (
                  <Menu
                    items={items}
                    trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} />}
                  />
                ) : null}
              </Row>
            );
          })}
        </Rows>
      )}

      {creating ? <RoleDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <RoleDialog role={editing} onClose={() => setEditing(null)} /> : null}
      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t("Delete {{name}}?", { name: confirmDelete?.name ?? "" })}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deleteRole.isPending}
        onConfirm={() => {
          if (!confirmDelete) {
            return;
          }
          deleteRole.mutate(confirmDelete.id, {
            onSuccess: () => {
              setConfirmDelete(null);
              toast.success(t("Role deleted"));
            },
          });
        }}
      />
    </div>
  );
}
